import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Walkthrough, useWalkthrough } from "./Walkthrough";
import { 
  LayoutDashboard,
  Package,
  ListChecks,
  FileText,
  BarChart3, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  Moon,
  Sun,
  ClipboardCheck,
  User,
  Layers,
  GitCompare,
  Activity,
  Brain,
  HelpCircle
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LOGO_URL = "/axisindia-logo-bg-removed.png";

const adminNavItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/product-types", label: "Product Types", icon: Layers },
  { path: "/admin/products", label: "Products", icon: Package },
  { path: "/admin/phases", label: "Phases", icon: ClipboardCheck },
  { path: "/admin/steps", label: "Verification Steps", icon: ListChecks },
  { path: "/admin/finetuning", label: "AI Finetuning", icon: Brain, highlight: true },
  { path: "/admin/reports", label: "Test Reports", icon: FileText },
  { path: "/admin/comparison", label: "Compare Tests", icon: GitCompare },
  { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/admin/users", label: "User Management", icon: User },
  { path: "/help", label: "Help Center", icon: HelpCircle },
];

const superadminNavItems = [
  { path: "/admin/noc", label: "NOC Dashboard", icon: Activity, highlight: true },
];

const testerNavItems = [
  { path: "/tester", label: "Dashboard", icon: LayoutDashboard },
  { path: "/tester/reports", label: "My Reports", icon: FileText },
  { path: "/help", label: "Help Center", icon: HelpCircle },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const { active: walkthroughActive, stopWalkthrough } = useWalkthrough();

  // Determine nav items based on role
  const getNavItems = () => {
    if (user?.role === "superadmin") {
      return [...superadminNavItems, ...adminNavItems];
    }
    if (user?.role === "admin") {
      return adminNavItems;
    }
    return testerNavItems;
  };
  
  const navItems = getNavItems();
  const userDashboard = (user?.role === "superadmin" || user?.role === "admin") ? "/admin" : "/tester";
  const roleDisplay = user?.role === "quality_engineer" ? "QC Engineer" : user?.role;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle("dark");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 border-r bg-card fixed top-0 left-0 h-screen z-30">
        <div className="p-4 border-b">
          <Link to={userDashboard} className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="AxisIndia" 
              className="h-8 w-auto object-contain"
            />
            <div>
              <span className="text-lg font-bold tracking-tight">VerifyPro</span>
              <p className="text-xs text-muted-foreground capitalize">{roleDisplay} Panel</p>
            </div>
          </Link>
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    item.highlight 
                      ? "bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/20" 
                      : isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 mb-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full justify-start" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 bg-card border-r flex flex-col animate-fade-in">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src={LOGO_URL} 
                  alt="AxisIndia" 
                  className="h-7 w-auto object-contain"
                />
                <span className="text-lg font-bold tracking-tight">VerifyPro</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <ScrollArea className="flex-1 py-4">
              <nav className="space-y-1 px-3">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        item.highlight 
                          ? "bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/20" 
                          : isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </ScrollArea>

            <div className="p-4 border-t">
              <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 lg:pl-64">
        {/* Top bar */}
        <header className="h-16 border-b bg-card flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold tracking-tight">
                {navItems.find(item => item.path === location.pathname)?.label || "Dashboard"}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} data-testid="theme-toggle">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 lg:p-6 max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
        {walkthroughActive && <Walkthrough role={user?.role} onClose={stopWalkthrough} />}
      </div>
    </div>
  );
}
